import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import type { EntitlementFeatureState } from "../api/admin";

function formatNumber(value: number | null): string {
  return value == null ? "—" : value.toLocaleString();
}

function formatUsage(feature: EntitlementFeatureState): string {
  if (feature.feature_type === "binary") {
    return feature.allowed ? "Included" : "Not included";
  }
  if (feature.limit == null) {
    return feature.used == null ? "Unlimited" : `${formatNumber(feature.used)} / Unlimited`;
  }
  return `${formatNumber(feature.used)} / ${formatNumber(feature.limit)}`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function statusColor(status: EntitlementFeatureState["status"]) {
  if (status === "available") {
    return { backgroundColor: "#F0FDF4", color: "#15803D" };
  }
  if (status === "near_limit" || status === "critical") {
    return { backgroundColor: "#FFF7ED", color: "#C2410C" };
  }
  if (status === "force_disabled" || status === "not_included" || status === "exhausted") {
    return { backgroundColor: "#FEF2F2", color: "#DC2626" };
  }
  return { backgroundColor: "#F1F5F9", color: "#475569" };
}

export default function EntitlementUsage({ features }: { features: EntitlementFeatureState[] }) {
  if (!features.length) {
    return <Alert severity="info">No entitlement state is available for this business.</Alert>;
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Entitlements
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Feature</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Used / limit</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Remaining</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Reset</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((feature) => (
              <TableRow key={feature.feature_key} sx={{ "&:last-child td": { border: 0 } }}>
                <TableCell sx={{ py: 1.15 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#0F172A" }}>
                    {feature.display_name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", fontFamily: "monospace" }}>
                    {feature.feature_key}
                  </Typography>
                  {feature.override?.reason && (
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748B", mt: 0.4 }}>
                      {feature.override.reason}
                    </Typography>
                  )}
                  {feature.portal_message && (
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748B", mt: 0.4 }}>
                      {feature.portal_message}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem", color: "#475569" }}>{formatUsage(feature)}</TableCell>
                <TableCell sx={{ fontSize: "0.78rem", color: "#475569" }}>{formatNumber(feature.remaining)}</TableCell>
                <TableCell sx={{ fontSize: "0.78rem", color: "#475569" }}>{formatDate(feature.reset_at)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                    <Chip
                      label={feature.status}
                      size="small"
                      sx={{ ...statusColor(feature.status), fontSize: "0.68rem", fontWeight: 700, height: 20 }}
                    />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      <Chip
                        label={feature.source}
                        size="small"
                        sx={{ backgroundColor: feature.source === "override" ? "#EFF6FF" : "#F1F5F9", color: feature.source === "override" ? "#2563EB" : "#64748B", fontSize: "0.66rem", height: 18 }}
                      />
                      {feature.cta && (
                        <Chip
                          label={feature.cta}
                          size="small"
                          sx={{ backgroundColor: "#FFF7ED", color: "#C2410C", fontSize: "0.66rem", height: 18 }}
                        />
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
